// src/components/BlogRichText.tsx
// Dependency-free, XSS-safe inline formatter for blog block text.
// Blocks store plain text with a tiny markdown subset: **bold**, *italic*,
// `code`, and [label](url). We HTML-escape first, then emit only our own
// controlled tags, and scheme-check every link so `javascript:`/`data:` URLs
// can never reach the DOM. Links render blue and open in a new tab.
import React, { useMemo } from "react";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function safeHref(raw: string): string | null {
  const u = raw.trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (/^mailto:/i.test(u)) return u;
  if (/^\//.test(u)) return u; // in-app relative link
  return null; // reject javascript:, data:, etc.
}

// Inline formatting on an already-escaped string.
function inline(text: string): string {
  let out = text;
  // links: [label](url) -> blue anchor
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label, url) => {
    const href = safeHref(url);
    if (!href) return label;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline;text-underline-offset:2px;">${label}</a>`;
  });
  // inline code: `code`
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  // bold: **text** (before italic so single * doesn't grab it)
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // italic: *text* or _text_
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/_([^_\n]+)_/g, "<em>$1</em>");
  // preserve manual line breaks inside a block
  out = out.replace(/\n/g, "<br/>");
  return out;
}

export function renderBlogInline(text: string): string {
  return inline(escapeHtml(text || ""));
}

// Renders a block's text with inline formatting. `as` picks the wrapper tag so
// headings and paragraphs share the same safe renderer.
export function BlogInline({ text, as = "span", className }: { text: string; as?: keyof JSX.IntrinsicElements; className?: string }) {
  const __html = useMemo(() => renderBlogInline(text), [text]);
  return React.createElement(as, { className, dangerouslySetInnerHTML: { __html } });
}

export default BlogInline;
